<template>
	<div
		:id="id"
		class="datepicker"
		:class="{ weekpicker: useAsWeekpicker === true }"
	>
		<div class="datepicker__date" :class="{ invalid: invalid === true }">
			<label class="datepicker__label" :for="id + '-textbox'">
				{{ label }}
			</label>
			<input
				:type="inputType"
				class="datepicker__input"
				placeholder="dd.mm.yyyy"
				:id="id + '-textbox'"
				aria-autocomplete="none"
				autocomplete="off"
				:aria-describedby="ariaDescribedBy"
				:aria-invalid="invalid === true ? 'true' : null"
				:value="value"
				@change="update($event.target.value)"
				ref="input"
			/>
			<input
				v-if="useAsWeekpicker"
				type="text"
				class="datepicker__input"
				placeholder="dd.mm.yyyy - dd.mm.yyyy"
				:id="id + '-textboxweek'"
				aria-autocomplete="none"
				autocomplete="off"
				:aria-describedby="ariaDescribedBy"
				:aria-invalid="invalid === true ? 'true' : null"
				:value="getWeekString"
				readonly
			/>
			<button
				class="datepicker__icon"
				:aria-label="ariaLabel"
				v-html="IconCalendar"
			></button>
		</div>
		<div
			:id="id + '-datepicker'"
			class="datepicker__dialog"
			role="dialog"
			aria-modal="true"
			aria-labelledby="id-dialog-label"
		>
			<div class="datepicker__header">
				<!-- <button
					class="datepicker__prev-year"
					aria-label="previous year"
				>
					<span v-html="IconArrow"></span>
				</button> -->
				<button
					class="datepicker__prev-month"
					:aria-label="ariaLabelPreviousMonth"
				>
					<span v-html="IconArrow"></span>
				</button>
				<h2
					:id="id + '-dialog-label'"
					class="datepicker__month-year"
					aria-live="polite"
				>
					{{ month }} {{ year }}
				</h2>
				<button
					class="datepicker__next-month"
					:aria-label="ariaLabelNextMonth"
				>
					<span v-html="IconArrow"></span>
				</button>
				<!-- <button class="datepicker__next-year" aria-label="next year">
					<span v-html="IconArrow"></span>
				</button> -->
			</div>
			<hr class="datepicker__divider" />
			<table
				:id="id + '-DatepickerGrid'"
				class="datepicker__dates"
				role="grid"
				:aria-labelledby="id + '-dialog-label'"
			>
				<thead>
					<tr v-if="siteLang == 'da'">
						<th scope="col" abbr="Mandag">M</th>
						<th scope="col" abbr="Tirsdag">T</th>
						<th scope="col" abbr="Onsdag">O</th>
						<th scope="col" abbr="Torsdag">T</th>
						<th scope="col" abbr="Fredag">F</th>
						<th scope="col" abbr="Lørdag">L</th>
						<th scope="col" abbr="Søndag">S</th>
					</tr>
					<tr v-else>
						<th scope="col" abbr="Monday">M</th>
						<th scope="col" abbr="Tuesday">T</th>
						<th scope="col" abbr="Wednesday">O</th>
						<th scope="col" abbr="Thurday">T</th>
						<th scope="col" abbr="Friday">F</th>
						<th scope="col" abbr="Saturday">L</th>
						<th scope="col" abbr="Sunday">S</th>
					</tr>
				</thead>
				<tbody></tbody>
			</table>
			<!-- <div class="datepicker__dialog-button-group">
				<button class="datepicker__dialog-button" value="cancel">
					Cancel
				</button>
				<button class="datepicker__dialog-button" value="ok">
					OK
				</button>
			</div> -->
			<!-- <div class="datepicker__message" aria-live="polite">
				Test
			</div> -->
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps, defineEmits } from "vue";
import "./datepicker";
import IconArrow from "../../../views/icons/arrow.cshtml";
import IconCalendar from "../../../views/icons/calendar.cshtml";

const props = defineProps({
	id: String,
	name: String,
	label: String,
	value: String,
	defaultDate: String,
	ariaLabel: String,
	describedBy: String,
	invalid: Boolean,
	disableInvalidUpdate: Boolean,
	useAsWeekpicker: Boolean,
});

const siteLang = ref(document.documentElement.lang);

const emit = defineEmits(["update", "invalid"]);

const ariaLabelPreviousMonth = computed(() => {
	return siteLang.value == "da" ? "Forrige måned" : "Previous month";
});

const ariaLabelNextMonth = computed(() => {
	return siteLang.value == "da" ? "Næste måned" : "Next month";
});

const month = computed(() => {
	return siteLang.value == "da" ? "Måned" : "Month";
});

const year = computed(() => {
	return siteLang.value == "da" ? "År" : "Year";
});

const ariaDescribedBy = computed(() => {
	return typeof props.describedBy != "undefined" ? props.describedBy : null;
});

const inputType = computed(() => {
	return props.useAsWeekpicker ? "hidden" : "text";
});

const getWeekString = computed(() => {
	const formattedDate = props.value
		? props.value.replace(/(\d{2})\.(\d{2})\.(\d{4})/, "$3-$2-$1")
		: new Date();

	const dateObject = new Date(formattedDate);
	const { monday, sunday } = utilities.getThisWeek(dateObject);

	return utilities.formatDate(monday) + " - " + utilities.formatDate(sunday);
});

const update = (date) => {
	if (!utilities.isDateValid(date)) {
		if (
			props.defaultDate != "" &&
			typeof props.defaultDate != "undefined" &&
			!props.disableInvalidUpdate
		) {
			inputNode.value = props.defaultDate;
		}
		if (!props.disableInvalidUpdate) {
			emit("update", props.defaultDate);
		} else {
			emit("invalid", date);
		}
	} else {
		emit("update", date);
	}
};

const inputNode = ref(null);

onMounted(() => {
	const datePickerEl = document.querySelector("#" + props.id);
	inputNode.value = datePickerEl.querySelector("input");
	const buttonNode = datePickerEl.querySelector("button");
	const dialogNode = datePickerEl.querySelector("[role=dialog]");

	const datePicker = new window.datepicker(
		inputNode.value,
		buttonNode,
		dialogNode
	);
	datePicker.init();
	inputNode.value.addEventListener("datepicker/change", (val) => {
		update(inputNode.value.value);
	});
});

</script>

<style lang="scss"></style>
